import { Animation, DynamicTexture, FileTools, IAnimationKey, IOfflineProvider, Nullable, Scene, Texture } from '@babylonjs/core';

export abstract class BaseDynamicImageTexture extends DynamicTexture {
    public constructor(name: string, image: HTMLImageElement | ImageBitmap, width: number, height: number, scene: Scene, samplingMode: number = Texture.TRILINEAR_SAMPLINGMODE, padding: number = 0) {
        super(
            name,
            { height: height + (2 * padding), width: width + (2 * padding) },
            scene,
            true,
            samplingMode
        );

        const context = this.getContext();
        context.drawImage(image, padding, padding, width, height);
        this.update();
    }
}

export class IconTexture extends BaseDynamicImageTexture {
    public constructor(name: string, image: HTMLImageElement | ImageBitmap, width: number, height: number, scene: Scene, samplingMode: number = Texture.TRILINEAR_SAMPLINGMODE) {
        super(name, image, width, height, scene, samplingMode, 2);

        if (width > height) {
            this.vScale = width / height;
            this.vOffset = -(width - height) / (2 * height);
        }
        else if (height > width) {
            this.uScale = height / width;
            this.uOffset = -(height - width) / (2 * width);
        }

        this.wrapU = Texture.CLAMP_ADDRESSMODE;
        this.wrapV = Texture.CLAMP_ADDRESSMODE;
        this.wrapR = Texture.CLAMP_ADDRESSMODE;
    }
}

export class AnimatedSpriteTexture extends BaseDynamicImageTexture {
    public constructor(name: string, image: HTMLImageElement | ImageBitmap, width: number, height: number, scene: Scene, nRows: number, nCols: number, frameRate: number, samplingMode: number = Texture.TRILINEAR_SAMPLINGMODE) {
        super(name, image, width, height, scene, samplingMode);

        this.vScale = 1 / nRows;
        this.uScale = 1 / nCols;

        // It seems that Babylon stops the animation right before reaching the last keyframe
        // (I suppose that this keyframe is only used to interpolate values in the interval between
        // the penultimate keyframe and the end of the animation).
        // We add a 'fake' last keyframe, so that when the animation stops right before it, the
        // last keyframe displayed is the 'true' last.
        const fakeLastKey: IAnimationKey = {
            frame: nRows * nCols,
            value: 0
        };

        if (nCols > 1) {
            const uAnimation = new Animation('uAnimation', 'uOffset', frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);

            const keys = new Array(nCols * nRows).fill(0).map((_, i) => ({
                frame: i,
                value: (i % nCols) / nCols
            } as IAnimationKey));

            keys.push(fakeLastKey);
            uAnimation.setKeys(keys);

            // Don't interpolate between frames
            uAnimation.floatInterpolateFunction = startValue => startValue;

            this.animations.push(uAnimation);
        }

        if (nRows > 1) {
            const vAnimation = new Animation('vAnimation', 'vOffset', frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);

            const keys = new Array(nRows).fill(0).map((_, i) => ({
                frame: i * nCols,
                value: (nRows - 1 - i) / nRows
            } as IAnimationKey));

            keys.push(fakeLastKey);
            vAnimation.setKeys(keys);

            // Don't interpolate between frames
            vAnimation.floatInterpolateFunction = startValue => startValue;

            this.animations.push(vAnimation);
        }
    }
}

export class ResizedTextureProvider {
    public constructor(maxWidth: number, maxHeight: number) {
        this._maxWidth = maxWidth;
        this._maxHeight = maxHeight;
    }

    public async createIconTextureAsync(name: string, url: string, scene: Scene, samplingMode: number = Texture.TRILINEAR_SAMPLINGMODE): Promise<IconTexture> {
        let texture = scene.getTextureByName(name);

        if (texture) {
            return texture as IconTexture;
        }

        let image: HTMLImageElement | ImageBitmap = null;

        try {
            image = await ResizedTextureProvider._fileToolsLoadSvgAsync(url, null);
        }
        catch (e) {
            image = await ResizedTextureProvider._fileToolsLoadSvgAsync(`${url}.svg`, null);
        }

        const { width, height } = this._computeNewDimensions(
            image,
            this._maxWidth,
            this._maxHeight
        );

        return new IconTexture(
            name,
            image,
            width,
            height,
            scene,
            samplingMode
        );
    }

    public async createAnimatedSpriteTextureAsync(name: string, url: string, scene: Scene, nRows: number, nCols: number, frameRate: number, samplingMode: number = Texture.TRILINEAR_SAMPLINGMODE): Promise<AnimatedSpriteTexture> {
        let image: HTMLImageElement | ImageBitmap = null;

        try {
            image = await ResizedTextureProvider._fileToolsLoadSvgAsync(url, null);
        }
        catch (e) {
            image = await ResizedTextureProvider._fileToolsLoadSvgAsync(`${url}.svg`, null);
        }

        const { width, height } = this._computeNewDimensions(
            image,
            this._maxWidth * nCols,
            this._maxHeight * nRows
        );

        return new AnimatedSpriteTexture(
            name,
            image,
            width,
            height,
            scene,
            nRows,
            nCols,
            frameRate,
            samplingMode
        );
    }


    private _computeNewDimensions(image: HTMLImageElement | ImageBitmap, maxWidth: number, maxHeight: number): { width: number, height: number } {
        const resizeWidthFactor = image.width / maxWidth;
        const resizeHeightFactor = image.height / maxHeight;

        return resizeWidthFactor > resizeHeightFactor
            ? { width: maxWidth, height: image.height / resizeWidthFactor }
            : { width: image.width / resizeHeightFactor, height: maxHeight };
    }

    private static async _fileToolsLoadSvgAsync(url: string, offlineProvider: Nullable<IOfflineProvider>, mimeType?: string): Promise<HTMLImageElement | ImageBitmap> {
        try {
            const file = await ResizedTextureProvider._fileToolsLoadFileAsync(url, offlineProvider);

            if (typeof (file.data) === 'string') {
                let svg = file.data;
                const svgDocument = new DOMParser().parseFromString(svg, 'image/svg+xml');
                const svgDocumentElement = svgDocument.documentElement as any;

                svgDocumentElement.width.baseVal.valueAsString = svgDocumentElement.width.baseVal.value
                    ? svgDocumentElement.width.baseVal.value.toString()
                    : svgDocumentElement.viewBox.baseVal.width.toString();

                svgDocumentElement.height.baseVal.valueAsString = svgDocumentElement.height.baseVal.value
                    ? svgDocumentElement.height.baseVal.value.toString()
                    : svgDocumentElement.viewBox.baseVal.height.toString();

                svg = new XMLSerializer().serializeToString(svgDocument);

                const base64EncodedSVG = 'data:image/svg+xml;base64,' + btoa(svg);
                return await ResizedTextureProvider._fileToolsLoadImageAsync(base64EncodedSVG, offlineProvider, mimeType);
            }
            else {
                return await ResizedTextureProvider._fileToolsLoadImageAsync(url, offlineProvider, mimeType);
            }
        }
        catch (e) {
            return await ResizedTextureProvider._fileToolsLoadImageAsync(url, offlineProvider, mimeType);
        }
    }

    private static _fileToolsLoadImageAsync(url: string, offlineProvider: Nullable<IOfflineProvider>, mimeType?: string): Promise<HTMLImageElement | ImageBitmap> {
        return new Promise((resolve, reject) => {
            FileTools.LoadImage(
                url,
                resolve,
                reject,
                offlineProvider,
                mimeType
            );
        });
    }

    private static _fileToolsLoadFileAsync(url: string, offlineProvider: Nullable<IOfflineProvider>, useArrayBuffer?: boolean): Promise<{ data: string | ArrayBuffer, responseURL?: string }> {
        return new Promise((resolve, reject) => {
            FileTools.LoadFile(
                url,
                (data: string | ArrayBuffer, responseURL?: string) => resolve({ data, responseURL }),
                null,
                offlineProvider,
                useArrayBuffer,
                reject
            );
        });
    }

    private readonly _maxWidth: number;
    private readonly _maxHeight: number;
}
